import { ReactElement } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import CompanyInfoPage from "./CompanyInfo";
import PasswordPage from "./Password";
import Billing from "./Billing";
import UsersPage from "./Users";
import AdvancedPage from "./Advanced";
import "./styles.css";
import Menu from "./Menu";
import { MenuOption } from "./types";
import { checkPrivilege } from "@utils/helpers";
import { Privileges, AccessLevels } from "../../../types";
import useWhoAmI from "./api/useWhoAmI";
import useFlags from "@common/hooks/useFlags";
import Licenses from "./Licenses";

type MenuOptionConfig = {
  path: string;
  element: ReactElement;
  privilege?: Privileges;
  accessLevel?: AccessLevels;
};

const EditAccount = (): JSX.Element => {
  const { data: user, error, isLoading } = useWhoAmI();
  const { settingsUsersV2Enable } = useFlags();

  if (isLoading || !user || error) {
    return <></>;
  }

  const { privileges } = user.attributes;

  const menuOptionsMap: Record<MenuOption, MenuOptionConfig> = {
    [MenuOption.Contact]: {
      path: "contact",
      element: <CompanyInfoPage />,
    },
    [MenuOption.Password]: {
      path: "password",
      element: <PasswordPage />,
    },
    [MenuOption.Billing]: {
      path: "billing",
      element: <Billing />,
      privilege: Privileges.SETTINGS_BILLING,
      accessLevel: AccessLevels.MANAGE,
    },
    [MenuOption.Users]: {
      path: "users/*",
      element: settingsUsersV2Enable ? <UsersPage /> : <Licenses />,
      privilege: Privileges.SETTINGS_USERS,
      accessLevel: AccessLevels.MANAGE,
    },
    [MenuOption.Advanced]: {
      path: "advanced",
      element: <AdvancedPage />,
      privilege: Privileges.SETTINGS_ACCOUNT,
      accessLevel: AccessLevels.MANAGE,
    },
  };

  const menuOptions = Object.entries(menuOptionsMap)
    .filter(([option]) => {
      const { privilege, accessLevel } = menuOptionsMap[option];
      return (
        !privilege ||
        checkPrivilege({
          userPrivileges: privileges || [],
          privilege,
          accessLevel,
        })
      );
    })
    .map(([option]) => option as MenuOption);

  return (
    <>
      <h1 data-testid="account-header" className="header">
        My Account
      </h1>
      <Row className="myAccount">
        <Col
          sm={3}
          className="left-menu4"
          style={{ padding: "20px 0px 0px 46px", maxWidth: "20%" }}
        >
          <Menu options={menuOptions} />
        </Col>

        <Col sm={9} style={{ padding: "25px 0px 0px" }}>
          <div className="tabContent">
            <Routes>
              {menuOptions.map((option) => {
                const config = menuOptionsMap[option];
                return (
                  <Route
                    key={option}
                    path={config.path}
                    element={config.element}
                  />
                );
              })}
              <Route
                index
                element={
                  <Navigate to={menuOptionsMap[menuOptions[0]].path} replace />
                }
              />
              <Route path="*" element={<div>Page not found</div>} />
            </Routes>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default EditAccount;
